const FUNNEL_ID_STORAGE_KEY = 'core_analysis_funnel_id'
const WEB_FUNNEL_ID_PATTERN = /^web_[A-Za-z0-9_]{8,120}$/

function newFunnelId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `web_${crypto.randomUUID().replace(/-/g, '_')}`
    }
  } catch {
    // crypto can throw in locked-down/insecure contexts; fall through.
  }
  return `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Session-scoped id joining every event in one analysis journey, read by
 * `analysisFunnelEnvelope` as `funnel_id`.
 *
 * Lives here rather than inside use-homepage-analysis so surfaces that fire a
 * funnel event *before* the hook has one (the upload CTA click, which happens
 * while the file picker is still open and no File exists yet) stamp the same id
 * the later upload/analyze events will carry. Idempotent: sessionStorage is the
 * source of truth, so call order between the hook and a component does not
 * matter.
 */
export function ensureAnalysisFunnelId(): string {
  try {
    const existing = window.sessionStorage.getItem(FUNNEL_ID_STORAGE_KEY)
    if (existing) return existing
    const created = newFunnelId()
    window.sessionStorage.setItem(FUNNEL_ID_STORAGE_KEY, created)
    return created
  } catch {
    // sessionStorage is unavailable (private mode, SSR). Analytics is
    // best-effort; an unpersisted id still groups the events in this call.
    return newFunnelId()
  }
}

/**
 * Continue a funnel that started on another device.
 *
 * The desktop QR handoff carries only this opaque session id. Validate it
 * before it becomes the phone session's active funnel so arbitrary query
 * values cannot pollute every later analysis event in that tab.
 */
export function adoptAnalysisFunnelId(candidate: string | null | undefined): string {
  const normalized = candidate?.trim() || ''
  if (!WEB_FUNNEL_ID_PATTERN.test(normalized)) return ensureAnalysisFunnelId()

  try {
    window.sessionStorage.setItem(FUNNEL_ID_STORAGE_KEY, normalized)
  } catch {
    // The validated id still joins this event when storage is unavailable.
  }
  return normalized
}
